import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Group } from '@visx/group';
import { BarStack } from '@visx/shape';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { LegendOrdinal } from '@visx/legend';
import { useTooltip, Tooltip, defaultStyles } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { useWindowSize } from '../useWindowSize';
import '../../css/Profit.css';

const defaultMargin = { top: 40, right: 30, bottom: 50, left: 80 };
const defaultColors = ['#6c5efb', '#c998ff'];

const VisxBudgetUsageBarChart = ({
  budgets,
  formatCurrency,
  animate = true,
}) => {
  const { width: windowWidth } = useWindowSize();
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 600, height: 400 });

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height: Math.max(400, width * 0.6) });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const data = Object.entries(budgets).map(([area, areaData]) => ({
    area: areaData.name,
    utilizado: areaData.used || 0,
    restante: Math.max(areaData.amount - (areaData.used || 0), 0),
  }));

  const keys = ['utilizado', 'restante'];

  const totals = useMemo(() => {
    return data.reduce((acc, curr) => {
      const total = keys.reduce((sum, key) => sum + curr[key], 0);
      return total > acc ? total : acc;
    }, 0);
  }, [data, keys]);

  const margin = useMemo(() => ({
    ...defaultMargin,
    left: Math.max(defaultMargin.left, dimensions.width * 0.15),
  }), [dimensions.width]);

  const xScale = useMemo(
    () =>
      scaleBand({
        range: [0, dimensions.width - margin.left - margin.right],
        domain: data.map((d) => d.area),
        padding: 0.2,
      }),
    [dimensions.width, margin.left, margin.right, data]
  );

  const yScale = useMemo(
    () =>
      scaleLinear({
        range: [dimensions.height - margin.top - margin.bottom, 0],
        domain: [0, totals],
        nice: true,
      }),
    [dimensions.height, margin.top, margin.bottom, totals]
  );

  const colorScale = useMemo(
    () =>
      scaleOrdinal({
        domain: keys,
        range: defaultColors,
      }),
    [keys]
  );

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
  } = useTooltip();

  const handleMouseOver = (event, datum) => {
    const coords = localPoint(event.target.ownerSVGElement, event);
    showTooltip({
      tooltipData: datum,
      tooltipLeft: coords.x,
      tooltipTop: coords.y,
    });
  };

  // Evita mostrar nada si el ancho de la ventana es demasiado pequeño
  const shouldRender = windowWidth >= 768;

  return shouldRender ? (
    <div className="visx-budget-chart" ref={containerRef}>
      <svg width={dimensions.width} height={dimensions.height}>
        <Group top={margin.top} left={margin.left}>
          <AxisLeft
            scale={yScale}
            tickFormat={formatCurrency}
            stroke="var(--text-color)"
            tickStroke="var(--text-color)"
            tickLabelProps={() => ({
              fill: 'var(--text-color)',
              fontSize: 11,
              textAnchor: 'end',
              dy: '0.33em',
            })}
          />
          <AxisBottom
            top={dimensions.height - margin.top - margin.bottom}
            scale={xScale}
            stroke="var(--text-color)"
            tickStroke="var(--text-color)"
            tickLabelProps={() => ({
              fill: 'var(--text-color)',
              fontSize: 11,
              textAnchor: 'middle',
            })}
          />
          <BarStack
            data={data}
            keys={keys}
            x={(d) => d.area}
            xScale={xScale}
            yScale={yScale}
            color={colorScale}
          >
            {(barStacks) =>
              barStacks.map((barStack) =>
                barStack.bars.map((bar) => (
                  <rect
                    key={`bar-stack-${barStack.index}-${bar.index}`}
                    x={bar.x}
                    y={bar.y}
                    height={bar.height}
                    width={bar.width}
                    fill={bar.color}
                    onMouseMove={(event) => handleMouseOver(event, bar)}
                    onMouseLeave={() => hideTooltip()}
                    className="visx-budget-chart__bar"
                  />
                ))
              )
            }
          </BarStack>
        </Group>
      </svg>
      <div className="visx-budget-chart__legend">
        <LegendOrdinal
          scale={colorScale}
          direction="row"
          labelMargin="0 15px 0 0"
        />
      </div>
      {tooltipOpen && tooltipData && (
        <Tooltip
          top={tooltipTop}
          left={tooltipLeft}
          className="visx-budget-chart__tooltip"
        >
          <div style={{ color: colorScale(tooltipData.key) }}>
            <strong>{tooltipData.key}</strong>
          </div>
          <div>{formatCurrency(tooltipData.bar.data[tooltipData.key])}</div>
          <div>
            <small>{tooltipData.bar.data.area}</small>
          </div>
        </Tooltip>
      )}
    </div>
  ) : null;
};

export default VisxBudgetUsageBarChart;
